import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { lectureApi } from '../../api/lectureApi'

// Async thunks
export const fetchLectures = createAsyncThunk(
  'lecture/fetchLectures',
  async (params, { rejectWithValue }) => {
    try {
      const response = await lectureApi.getLectures(params)
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch lectures'
      )
    }
  }
)

export const fetchLectureDetails = createAsyncThunk(
  'lecture/fetchLectureDetails',
  async (lectureId, { rejectWithValue }) => {
    try {
      const response = await lectureApi.getLectureDetails(lectureId)
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch lecture details'
      )
    }
  }
)

export const createLecture = createAsyncThunk(
  'lecture/createLecture',
  async (lectureData, { rejectWithValue }) => {
    try {
      const response = await lectureApi.createLecture(lectureData)
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to create lecture'
      )
    }
  }
)

export const updateLecture = createAsyncThunk(
  'lecture/updateLecture',
  async ({ lectureId, data }, { rejectWithValue }) => {
    try {
      const response = await lectureApi.updateLecture(lectureId, data)
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update lecture'
      )
    }
  }
)

export const updateMeetingLink = createAsyncThunk(
  'lecture/updateMeetingLink',
  async ({ lectureId, meetingData }, { rejectWithValue }) => {
    try {
      const response = await lectureApi.updateMeetingLink(lectureId, meetingData)
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update meeting link'
      )
    }
  }
)

export const scheduleLectures = createAsyncThunk(
  'lecture/scheduleLectures',
  async (scheduleData, { rejectWithValue }) => {
    try {
      const response = await lectureApi.scheduleLectures(scheduleData)
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to schedule lectures'
      )
    }
  }
)

export const fetchUpcomingLectures = createAsyncThunk(
  'lecture/fetchUpcomingLectures',
  async (params, { rejectWithValue }) => {
    try {
      const response = await lectureApi.getUpcomingLectures(params)
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch upcoming lectures'
      )
    }
  }
)

const initialState = {
  lectures: [],
  upcomingLectures: [],
  currentLecture: null,
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
}

const lectureSlice = createSlice({
  name: 'lecture',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearCurrentLecture: (state) => {
      state.currentLecture = null
    },
    resetLectureState: (state) => {
      state.lectures = []
      state.upcomingLectures = []
      state.currentLecture = null
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Lectures
      .addCase(fetchLectures.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchLectures.fulfilled, (state, action) => {
        state.isLoading = false
        state.lectures = action.payload.data || action.payload
        state.pagination = {
          page: action.payload.meta?.page || 1,
          limit: action.payload.meta?.limit || 10,
          total: action.payload.meta?.total || 0,
        }
      })
      .addCase(fetchLectures.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      // Fetch Lecture Details
      .addCase(fetchLectureDetails.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchLectureDetails.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentLecture = action.payload
      })
      .addCase(fetchLectureDetails.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      // Create Lecture
      .addCase(createLecture.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(createLecture.fulfilled, (state, action) => {
        state.isLoading = false
        state.lectures.unshift(action.payload)
      })
      .addCase(createLecture.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      // Update Lecture
      .addCase(updateLecture.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateLecture.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentLecture = action.payload
        // Update in lectures list if exists
        const index = state.lectures.findIndex(l => l._id === action.payload._id)
        if (index !== -1) {
          state.lectures[index] = action.payload
        }
      })
      .addCase(updateLecture.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      // Update Meeting Link
      .addCase(updateMeetingLink.fulfilled, (state, action) => {
        state.currentLecture = action.payload
        // Update in lectures list if exists
        const index = state.lectures.findIndex(l => l._id === action.payload._id)
        if (index !== -1) {
          state.lectures[index] = action.payload
        }
      })

      // Schedule Lectures
      .addCase(scheduleLectures.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(scheduleLectures.fulfilled, (state, action) => {
        state.isLoading = false
        // Add new lectures to the list
        if (Array.isArray(action.payload)) {
          state.lectures = [...action.payload, ...state.lectures]
        }
      })
      .addCase(scheduleLectures.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      // Fetch Upcoming Lectures
      .addCase(fetchUpcomingLectures.fulfilled, (state, action) => {
        state.upcomingLectures = action.payload
      })
  },
})

export const { clearError, clearCurrentLecture, resetLectureState } = lectureSlice.actions
export default lectureSlice.reducer